// src/services/categoryService.js
import { repo } from "../dbservice/repo";
import { CategoryModel } from "../models/CategoryModel";

class CategoryService {
  constructor() {
    this.collectionName = "categories";
  }

  get currentUser() {
    if (typeof window === "undefined") return null;
    try {
      const data = localStorage.getItem("userData");
      return data ? JSON.parse(data) : null;
    } catch (e) {
      console.error("[CategoryService] Failed to parse userData", e);
      return null;
    }
  }

  /**
   * Create a new category with automatic audit stamping.
   */
  async create(data, currentUser = {}) {
    const user = currentUser?.email ? currentUser : this.currentUser;
    const targetId = data.id || data.categoryId || crypto.randomUUID();

    const payload = {
      ...data,
      id: targetId,
      categoryId: data.categoryId || targetId,
      companyId: data.companyId || user?.companyId || null,
      metadata: {
        ...(data.metadata || {}),
        createdBy: user?.email || "system",
        updatedBy: user?.email || "system",
      },
    };

    return await this.upsertData(targetId, payload, { isCreation: true });
  }

  /**
   * Fetch categories scoped to the current user's company.
   */
  async fetchCategories({ companyId = "", status = "" } = {}) {
    const user = this.currentUser;
    if (!companyId || companyId.toString().trim() === "") {
      companyId = user?.companyId;
    }

    const filters = [];
    if (companyId) {
      filters.push({ field: "companyId", value: String(companyId) });
    }
    if (status) {
      filters.push({ field: "status", value: status });
    }

    try {
      return (await repo.fetchData(this.collectionName, filters)) || [];
    } catch (error) {
      console.error("[CategoryService] Failed to fetch categories:", error);
      return [];
    }
  }

  /**
   * Fetch a single category by ID.
   */
  async fetchById(id) {
    if (!id) {
      console.error("[CategoryService] fetchById: ID missing");
      return null;
    }
    try {
      return (await repo.fetchById(this.collectionName, id)) || null;
    } catch (error) {
      console.error(`[CategoryService] Failed to fetch category ${id}:`, error);
      return null;
    }
  }

  /**
   * Update an existing category — preserves historical creation metadata.
   */
  async update(id, data, currentUser = {}) {
    if (!id) throw new Error("Category ID is required for update.");
    const user = currentUser?.email ? currentUser : this.currentUser;

    const payload = {
      ...data,
      id,
      metadata: {
        ...(data.metadata || {}),
        updatedBy: user?.email || "system",
      },
    };

    return await this.upsertData(id, payload, { isCreation: false });
  }

  /**
   * Core persistence orchestrator with read-before-write metadata preservation.
   */
  async upsertData(targetId, data, { isCreation = false } = {}) {
    if (!targetId) throw new Error("Target Category ID is required");

    let finalData = { ...data };

    if (!isCreation) {
      const existing = await this.fetchById(targetId);
      if (existing) {
        finalData = {
          ...existing,
          ...data,
          metadata: {
            ...(existing.metadata || {}),
            ...(data.metadata || {}),
            createdAt:
              existing.metadata?.createdAt ||
              existing.createdAt ||
              new Date().toISOString(),
            createdBy:
              existing.metadata?.createdBy || existing.createdBy || "system",
          },
        };
      }
    }

    const instance = new CategoryModel(
      { ...finalData, id: targetId },
      { isCreation }
    );

    if (!instance.isValid()) {
      throw new Error(
        "Invalid category payload: categoryId must be 2-5 uppercase letters, and categoryName is required."
      );
    }

    const payload = instance.toPayload();
    const nowIso = new Date().toISOString();

    payload.metadata = {
      ...(payload.metadata || {}),
      updatedAt: nowIso,
      updatedBy:
        data.metadata?.updatedBy || payload.metadata?.updatedBy || "system",
    };

    return await repo.upsertData(this.collectionName, targetId, payload);
  }

  /**
   * Delete a category record.
   */
  async delete(id) {
    if (!id) throw new Error("Category ID is required for deletion.");
    try {
      await repo.deleteData(this.collectionName, id);
      return { success: true };
    } catch (error) {
      console.error(`[CategoryService] Failed to delete category ${id}:`, error);
      throw error;
    }
  }
}

export const categoryService = new CategoryService();